/**
 * MA-ARCH-178: Landing Page Performance Component
 * 
 * Reusable performance display for landing page analytics.
 * Shows KPI cards, conversion chain, section reach bars, and effectiveness status.
 * 
 * Usage:
 *   <LandingPagePerformance versionId={id} profitPathId={ppId} />
 *   <LandingPagePerformance versionId={id} compact />  // for builder strip
 */

import { useQuery } from "@tanstack/react-query";
import { TrendingUp, Eye, MousePointerClick, PlayCircle, Send, Users, BarChart3, AlertCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";
import AttributionCoverageSummary from "@/components/attribution/AttributionCoverageSummary";

// =============================================================================
// Hooks
// =============================================================================

function useLPSummary(versionId, profitPathId) {
  return useQuery({
    queryKey: ["lp-analytics-summary", versionId, profitPathId],
    queryFn: async () => {
      const params = new URLSearchParams({ landing_page_version_id: versionId });
      if (profitPathId) params.append("profit_path_id", profitPathId);
      const res = await api.get(`/landing-page-analytics/summary?${params}`);
      return res.data;
    },
    enabled: !!versionId,
    staleTime: 30000,
  });
}

function useLPSections(versionId) {
  return useQuery({
    queryKey: ["lp-analytics-sections", versionId],
    queryFn: async () => {
      const res = await api.get(`/landing-page-analytics/sections?landing_page_version_id=${versionId}`);
      return res.data;
    },
    enabled: !!versionId,
    staleTime: 30000,
  });
}

// =============================================================================
// Compact Strip (for Builder)
// =============================================================================

export function LandingPagePerformanceStrip({ versionId, profitPathId }) {
  const { data, isLoading } = useLPSummary(versionId, profitPathId);

  if (!versionId) return null;
  if (isLoading) {
    return (
      <div className="flex items-center gap-3 px-3 py-2 bg-slate-800/30 rounded-lg border border-slate-700/20 animate-pulse" data-testid="lp-perf-strip-loading">
        <div className="h-3 w-16 bg-slate-700 rounded" />
        <div className="h-3 w-16 bg-slate-700 rounded" />
        <div className="h-3 w-16 bg-slate-700 rounded" />
      </div>
    );
  }

  if (!data) return null;

  const effectiveness = data.effectiveness;
  const statusConfig = getStatusConfig(effectiveness);

  return (
    <div className="flex items-center gap-3 px-3 py-2 bg-slate-800/30 rounded-lg border border-slate-700/20" data-testid="lp-perf-strip">
      <div className="flex items-center gap-1.5 text-xs text-slate-400">
        <Eye className="h-3 w-3" />
        <span data-testid="lp-strip-views">{data.unique_sessions}</span>
      </div>
      <div className="w-px h-3 bg-slate-700" />
      <div className="flex items-center gap-1.5 text-xs text-slate-400">
        <MousePointerClick className="h-3 w-3" />
        <span data-testid="lp-strip-cta-rate">{formatRate(data.rates?.cta_click_rate)}</span>
      </div>
      <div className="w-px h-3 bg-slate-700" />
      <div className="flex items-center gap-1.5 text-xs text-slate-400">
        <PlayCircle className="h-3 w-3" />
        <span data-testid="lp-strip-funnel-rate">{formatRate(data.rates?.funnel_start_rate)}</span>
      </div>
      <div className="w-px h-3 bg-slate-700" />
      <Badge className={cn("h-5 text-[10px] border-0", statusConfig.className)} data-testid="lp-strip-status">
        {statusConfig.label}
      </Badge>
    </div>
  );
}

// =============================================================================
// Full Performance Panel (for Profit Path Detail)
// =============================================================================

export default function LandingPagePerformance({ versionId, profitPathId, compact = false }) {
  const { data: summary, isLoading: summaryLoading } = useLPSummary(versionId, profitPathId);
  const { data: sections, isLoading: sectionsLoading } = useLPSections(versionId);

  if (!versionId) {
    return (
      <div className="p-6 text-center text-slate-500 text-sm" data-testid="lp-perf-no-version">
        No active landing page version
      </div>
    );
  }

  if (summaryLoading || sectionsLoading) {
    return (
      <div className="space-y-4 animate-pulse" data-testid="lp-perf-loading">
        <div className="grid grid-cols-4 gap-3">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-20 bg-slate-800/30 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (!summary) return null;

  const effectiveness = summary.effectiveness;
  const statusConfig = getStatusConfig(effectiveness);
  const isLowData = effectiveness === "low_data";
  const rates = summary.rates || {};

  return (
    <div className="space-y-5" data-testid="lp-performance-panel">
      {/* Effectiveness Status */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-slate-400" />
          <span className="text-sm font-medium text-slate-200">Performance</span>
        </div>
        <Badge className={cn("h-6 text-xs px-3 border", statusConfig.className)} data-testid="lp-perf-status">
          {statusConfig.label}
        </Badge>
      </div>

      {isLowData && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20" data-testid="lp-perf-low-data-warning">
          <AlertCircle className="h-4 w-4 text-amber-400 flex-shrink-0" />
          <p className="text-xs text-amber-300">
            Not enough data yet ({summary.unique_sessions} sessions, need {summary.thresholds?.min_page_views} for reliable metrics).
          </p>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="lp-perf-kpis">
        <KpiCard
          icon={<Eye className="h-4 w-4" />}
          label="Page Views"
          value={summary.unique_sessions}
          subtext={`${summary.page_views} total`}
          testId="lp-kpi-views"
        />
        <KpiCard
          icon={<MousePointerClick className="h-4 w-4" />}
          label="CTA Click Rate"
          value={formatRate(rates.cta_click_rate)}
          subtext={`${summary.cta_click_sessions} clicks`}
          muted={isLowData}
          testId="lp-kpi-cta"
        />
        <KpiCard
          icon={<PlayCircle className="h-4 w-4" />}
          label="Funnel Start Rate"
          value={formatRate(rates.funnel_start_rate)}
          subtext={`${summary.funnel_start_sessions} starts`}
          muted={isLowData}
          testId="lp-kpi-funnel-start"
        />
        <KpiCard
          icon={<Send className="h-4 w-4" />}
          label="Funnel Submit Rate"
          value={formatRate(rates.funnel_submit_rate)}
          subtext={`${summary.funnel_submit_sessions} submits`}
          muted={isLowData}
          testId="lp-kpi-funnel-submit"
        />
      </div>

      {/* Lead attribution */}
      {(summary.lead_count > 0 || !isLowData) && (
        <div className="flex items-center gap-3 px-3 py-2 bg-slate-800/30 rounded-lg border border-slate-700/20">
          <Users className="h-4 w-4 text-slate-400" />
          <div className="flex-1">
            <span className="text-sm text-slate-300" data-testid="lp-lead-count">{summary.lead_count} leads</span>
            <span className="text-xs text-slate-600 ml-2">
              {formatRate(rates.landing_page_to_lead_rate)} conversion
            </span>
          </div>
          {summary.lead_linkage_method !== "none" && (
            <span className="text-[10px] text-slate-600">
              via {summary.lead_linkage_method === "session_match" ? "session" : "profit path"}
            </span>
          )}
        </div>
      )}

      {/* MA-ARCH-181: Attribution Coverage for this Landing Page */}
      {summary.lead_count > 0 && (
        <div className="px-3 py-2 bg-slate-800/30 rounded-lg border border-slate-700/20" data-testid="lp-attribution-coverage">
          <AttributionCoverageSummary landingPageVersionId={versionId} compact />
        </div>
      )}

      {/* Scroll Depth */}
      {summary.scroll_summary?.average_depth != null && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-500 font-medium">Scroll Depth</span>
            <span className="text-xs text-slate-400" data-testid="lp-scroll-avg">avg {summary.scroll_summary.average_depth}%</span>
          </div>
          <div className="flex gap-2">
            {Object.entries(summary.scroll_summary.distribution || {}).map(([bucket, count]) => {
              const pct = summary.scroll_summary.total_sessions > 0
                ? Math.round(count / summary.scroll_summary.total_sessions * 100)
                : 0;
              return (
                <div key={bucket} className="flex-1 text-center">
                  <div className="h-8 bg-slate-800/50 rounded relative overflow-hidden">
                    <div
                      className="absolute bottom-0 left-0 right-0 bg-indigo-500/30 rounded transition-all"
                      style={{ height: `${pct}%` }}
                    />
                  </div>
                  <span className="text-[10px] text-slate-600 mt-0.5 block">{bucket}%</span>
                  <span className="text-[10px] text-slate-400">{pct}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Section Reach */}
      {sections?.sections?.length > 0 && (
        <div className="space-y-2" data-testid="lp-section-reach">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-3.5 w-3.5 text-slate-500" />
            <span className="text-xs text-slate-500 font-medium">Section Reach</span>
          </div>
          <div className="space-y-1.5">
            {sections.sections.map((s) => (
              <div key={s.section_key} className="flex items-center gap-2" data-testid={`lp-section-${s.section_key}`}>
                <span className="text-xs text-slate-500 w-24 capitalize">
                  {s.section_key.replace("_", " ")}
                </span>
                <div className="flex-1 h-4 bg-slate-800/50 rounded overflow-hidden">
                  <div
                    className={cn(
                      "h-full rounded transition-all",
                      s.reach_percentage >= 50 ? "bg-emerald-500/40" :
                        s.reach_percentage >= 25 ? "bg-amber-500/40" : "bg-red-500/40"
                    )}
                    style={{ width: `${Math.max(s.reach_percentage, 2)}%` }}
                  />
                </div>
                <span className="text-xs text-slate-400 w-10 text-right">
                  {s.reach_percentage}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Version Summary Row (for version list)
// =============================================================================

export function LandingPageVersionMetrics({ versionId, profitPathId }) {
  const { data, isLoading } = useLPSummary(versionId, profitPathId);

  if (isLoading) {
    return <span className="text-xs text-slate-600">Loading...</span>;
  }

  if (!data || data.unique_sessions === 0) {
    return <span className="text-xs text-slate-600">No data</span>;
  }

  const statusConfig = getStatusConfig(data.effectiveness);

  return (
    <div className="flex items-center gap-3 text-xs" data-testid={`lp-version-metrics-${versionId}`}>
      <span className="text-slate-400">{data.unique_sessions} views</span>
      <span className="text-slate-400">{data.funnel_start_sessions} starts</span>
      <span className="text-slate-400">{formatRate(data.rates?.funnel_start_rate)}</span>
      <Badge className={cn("h-4 text-[9px] border-0", statusConfig.className)}>
        {statusConfig.label}
      </Badge>
    </div>
  );
}

// =============================================================================
// Helpers
// =============================================================================

function KpiCard({ icon, label, value, subtext, muted = false, testId }) {
  return (
    <div className={cn(
      "p-3 rounded-lg bg-slate-800/30 border border-slate-700/20",
      muted && "opacity-60"
    )} data-testid={testId}>
      <div className="flex items-center gap-1.5 text-slate-500 mb-1">
        {icon}
        <span className="text-[10px] font-medium uppercase tracking-wider">{label}</span>
      </div>
      <div className="text-lg font-semibold text-slate-200">{value}</div>
      {subtext && <div className="text-[10px] text-slate-600">{subtext}</div>}
    </div>
  );
}

function formatRate(rate) {
  if (rate == null) return "—";
  return `${Math.round(rate * 100)}%`;
}

function getStatusConfig(status) {
  switch (status) {
    case "strong":
      return { label: "Strong", className: "bg-emerald-500/20 text-emerald-400" };
    case "average":
      return { label: "Average", className: "bg-amber-500/20 text-amber-400" };
    case "weak":
      return { label: "Weak", className: "bg-red-500/20 text-red-400" };
    default:
      return { label: "Low Data", className: "bg-slate-500/20 text-slate-400" };
  }
}
